<template>
  <div id="fee-tab">
    <div class="fee-list">
      <table>
        <thead>
          <th>{{ $t('ID') }}</th>
          <th>{{ $t('DATE') }}</th>
          <th>{{ $t('FEE_TYPE') }}</th>
          <th>{{ $t('AMOUNT') }}</th>
          <th class="actions">
          </th>
        </thead>
        <tbody>
          <template v-for="(fee, index) in fees">
            <tr :key="fee.id" v-if="!!fee.status" class="registered" :class="{ retracted: (fee.status === FEE_STATUSES.RETRACTED) }">
              <td>{{ (fees.length - index) }}</td>
              <td>{{ fee.registeredOn }}</td>
              <td>{{ $t(fee.type) }}</td>
              <td>{{ addCommas(fee.amount) }}</td>
              <td class="actions">
                <font-awesome-icon v-if="false" class="action-icon" :title="$t('REMOVE_FEE')" :icon="faTrashAlt" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <confirm-modal />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';

import ConfirmModal from '@/components/ConfirmModal';

import { hasPermissions } from '@/helpers/permission';
import { CONTRACT_STATUSES, PERMISSIONS, FEE_TYPES, FEE_STATUSES, MILLION, DATE_FORMAT, DISPLAY_DATE_FORMAT } from '@/consts';
import { addCommas } from '@/helpers';

import ContractService from '../../ContractDetailServices';

const { useState } = createNamespacedHelpers('contractDetail');
const { useActions: useContractActions } = createNamespacedHelpers('contractDetail');

export default {
  name: 'FeeTab',
  components: {
    ConfirmModal
  },
  setup() {
    const { targetingContract: contract } = useState(['targetingContract']);
    const { getContractDetail } = useContractActions(['getContractDetail']);

    const fees = ref([]);

    const updateContract = () => {
      getContractDetail(contract.value.publicId);
    };

    const canUndoContract = computed(() => hasPermissions([PERMISSIONS.UNDO_CONTRACT]));

    const fetchFees = async () => {
      const items = [];
      if (contract.value.roundingFee > 0) {
        items.push({
          registeredOn: contract.value.submittedOn,
          type: 'ROUNDING_FEE',
          status: 'REGISTERED',
          amount: contract.value.roundingFee
        });
      }

      if (contract.value.renewalDifference < 0) {
        items.push({
          registeredOn: contract.value.submittedOn,
          type: 'OLD_CNTRCT_PENALTY',
          status: 'REGISTERED',
          amount: Math.abs(contract.value.renewalDifference)
        });
      }

      const items_ = await ContractService.getFeeList(contract.value.id);

      fees.value = [
        ...items,
        ...items_
      ].map((item) => ({
        ...item,
        registeredOn: moment(item.registeredOn, DATE_FORMAT).format(DISPLAY_DATE_FORMAT),
      }));
    };

    onMounted(() => {
      fetchFees();
    });

    return {
      contract,
      updateContract,
      CONTRACT_STATUSES,
      canUndoContract,
      FEE_STATUSES,
      FEE_TYPES,
      MILLION,
      addCommas,
      fees
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#fee-tab {
  /deep/ table {
    width: 100%;
    border-collapse: inherit;
    border-spacing: 0;
    background-color: $ghostWhite;

    thead {
      text-transform: uppercase;
      font-weight: bold;
      text-align: center;
      th {
        background-color: $brightMaroon;
        color: $white;
        padding: 15px 10px;
        border-top: 1px solid $alto;
        border-bottom: 1px solid $alto;
        font-size: 14px;

        &:first-child {
          border-top-left-radius: 3px;
          border-bottom-left-radius: 3px;
          border-left: 1px solid $alto;

        }

        &:last-child {
          border-top-right-radius: 3px;
          border-bottom-right-radius: 3px;
          border-right: 1px solid $alto;
        }
      }
    }

    tbody {
      text-align: center;

      tr {
        background-color: $babyPowder;
        transition: all .2s ease-in-out;

        &:hover {
          background-color: $ghostWhite;
        }

        td {
          padding: 15px 5px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;
          background-color: $babyPowder;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;
          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
            padding-right: 20px;
          }

          .icon {
            margin-right: 10px;
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
